import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
export function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: LegalModalProps) {
  return <AnimatePresence data-id="element-92">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-charcoal/40 backdrop-blur-sm z-50" data-id="element-93" />
          <motion.div initial={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[80vh] bg-cream-50 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden" data-id="element-94">
            <div className="flex items-center justify-between p-6 border-b border-cream-300" data-id="element-95">
              <h2 className="text-xl font-serif font-bold text-charcoal" data-id="element-96">
                {title}
              </h2>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-cream-200 transition-colors text-charcoal-light" data-id="element-97">
                <X className="w-5 h-5" data-id="element-98" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto text-charcoal-light leading-relaxed text-sm space-y-4" data-id="element-99">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function PrivacyContent() {
  return <>
      <p className="text-xs text-charcoal-light/60" data-id="element-100">
        Última actualización: febrero 2026
      </p>

      <h3 className="font-bold text-charcoal text-base" data-id="element-101">
        1. Información que recopilamos
      </h3>
      <p data-id="element-102">
        Al adquirir "Mis Manos, Mi Calma", recopilamos únicamente la información
        necesaria para procesar tu compra: nombre, dirección de correo
        electrónico y datos de pago. No almacenamos datos de tarjetas de crédito
        en nuestros servidores.
      </p>

      <h3 className="font-bold text-charcoal text-base" data-id="element-103">
        2. Uso de la información
      </h3>
      <p data-id="element-104">Tu información personal se utiliza exclusivamente para:</p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-105">
        <li data-id="element-106">Procesar y entregar tu compra</li>
        <li data-id="element-107">Enviarte el enlace de descarga del PDF</li>
        <li data-id="element-108">Comunicarnos contigo en caso de problemas con tu pedido</li>
      </ul>
      <p data-id="element-109">
        No vendemos, alquilamos ni compartimos tu información personal con
        terceros con fines comerciales.
      </p>

      <h3 className="font-bold text-charcoal text-base" data-id="element-110">
        3. Protección de datos
      </h3>
      <p data-id="element-111">
        Implementamos medidas de seguridad técnicas y organizativas para
        proteger tu información personal contra acceso no autorizado, pérdida o
        alteración.
      </p>

      <h3 className="font-bold text-charcoal text-base" data-id="element-112">4. Cookies</h3>
      <p data-id="element-113">
        Este sitio puede utilizar cookies esenciales para el funcionamiento
        básico de la página y el procesamiento de pagos. No utilizamos cookies
        de seguimiento publicitario.
      </p>

      <h3 className="font-bold text-charcoal text-base" data-id="element-114">5. Tus derechos</h3>
      <p data-id="element-115">
        Podés solicitar la eliminación de tus datos personales en cualquier
        momento escribiéndonos por correo electrónico. Responderemos a tu
        solicitud en un plazo máximo de 30 días.
      </p>

      <h3 className="font-bold text-charcoal text-base" data-id="element-116">
        6. Cambios en esta política
      </h3>
      <p data-id="element-117">
        Nos reservamos el derecho de actualizar esta política de privacidad.
        Cualquier cambio será publicado en esta misma página.
      </p>
    </>;
}
export function TermsContent() {
  return <>
      <p className="text-xs text-charcoal-light/60" data-id="element-118">
        Última actualización: febrero 2026
      </p>

      <h3 className="font-bold text-charcoal text-base" data-id="element-119">1. Producto digital</h3>
      <p data-id="element-120">
        "Mis Manos, Mi Calma" es un producto digital en formato PDF. Al
        completar la compra, recibirás acceso inmediato para descargar el
        archivo. Este producto es para uso personal y no puede ser
        redistribuido, revendido ni compartido.
      </p>

      <h3 className="font-bold text-charcoal text-base" data-id="element-121">
        2. Naturaleza informativa
      </h3>
      <p data-id="element-122">
        El contenido de este PDF es de carácter informativo y educativo. No
        constituye consejo médico, psicológico ni profesional de salud. Si tenés
        síntomas severos o condiciones médicas, consultá con un profesional de
        la salud.
      </p>

      <h3 className="font-bold text-charcoal text-base" data-id="element-123">
        3. Política de reembolso
      </h3>
      <p data-id="element-124">
        Dado que se trata de un producto digital de descarga inmediata, no se
        aceptan devoluciones una vez realizada la descarga. Si tenés algún
        inconveniente técnico con el archivo, escribinos y lo resolveremos.
      </p>

      <h3 className="font-bold text-charcoal text-base" data-id="element-125">
        4. Propiedad intelectual
      </h3>
      <p data-id="element-126">
        Todo el contenido de "Mis Manos, Mi Calma" — incluyendo textos, diseños,
        ilustraciones y metodología — está protegido por derechos de autor.
        Queda prohibida su reproducción total o parcial sin autorización
        expresa.
      </p>

      <h3 className="font-bold text-charcoal text-base" data-id="element-127">
        5. Limitación de responsabilidad
      </h3>
      <p data-id="element-128">
        Los resultados pueden variar de persona a persona. No garantizamos
        resultados específicos. El uso del protocolo es bajo tu propia
        responsabilidad.
      </p>

      <h3 className="font-bold text-charcoal text-base" data-id="element-129">6. Jurisdicción</h3>
      <p data-id="element-130">
        Estos términos se rigen por las leyes de la República Argentina.
        Cualquier disputa será sometida a los tribunales competentes de la
        Ciudad Autónoma de Buenos Aires.
      </p>
    </>;
}